import { useTimesheets } from '../../store/timesheets'
import { storeToRefs } from 'pinia'
import { SyncTimesheet, Timer, Timers, Timesheet, Timesheets } from '../../types/timers'
import { useExpenses } from '../../store/useExpenses'
import { useAuthenticateSAML } from '../../store/useAuthenticateSAML'
import { formatISO, isSameDay } from 'date-fns'
import { grabDatesFromTimesheets } from './utils'
import { mapTimersForSync } from './timersSync'
import { useUserStore } from '../../store/useUserStore'
import { useTimers } from '../../store/timers'
import { isValidDateObject, logInfo } from '../../utils'
import { TimesheetsModel } from '../../models/TimesheetsModel'

export const timesheetSync = () => {
  const { hasReadyToSyncTimesheets } = storeToRefs(useTimesheets())
  const { getReadyToSyncTimesheets, removeBeyondPayPeriod } = useTimesheets()

  const getTimesheetsToSync = (): SyncTimesheet[] => {
    const timesheets = getReadyToSyncTimesheets()
    const { getExpensesByDate } = useExpenses()
    const { getJWT } = storeToRefs(useAuthenticateSAML())
    const user = storeToRefs(useUserStore())
    const staffing_user_id = getJWT.value.staffing_users_id
    return timesheets
      .map((timesheet): SyncTimesheet => {
        let theDate: Date | null = null
        let isDateValid = false
        try {
          theDate = new Date(timesheet.date)
          isDateValid = isValidDateObject(theDate)
        } catch (e) {
          logInfo(
            'error',
            'Invalid timesheet date',
            'Invalid timesheet date found when syncing, skipping the timesheet',
            { timesheet, date: timesheet.date }
          )
          // @ts-expect-error We filter out the nulls after the mapping call
          return null
        }
        return {
          staffing_user_id,
          date: isDateValid ? formatISO(theDate) : '',
          deviceDate: timesheet.deviceDate,
          //todo: remove below
          //@ts-expect-error undefined not normally ok
          activity_data: mapTimersForSync(timesheet.timers as Timers, user),
          ...(timesheet.originalTimers && { original_timers: timesheet.originalTimers }),
          ...(timesheet.editHistory && { edit_history: timesheet.editHistory }),
          expenses: getExpensesByDate(theDate),
          claim_travel_override: timesheet.claim_travel_override!,
          agree_legal: timesheet.agree_legal!,
        }
      })
      .filter((timesheet) => !!timesheet)
  }

  /**Compare new timesheets and set only new ones by date */
  const saveNewTimesheets = async (timesheets: Timesheets) => {
    const { getTimesheets: getTimesheetsFromStore } = storeToRefs(useTimesheets())
    const dates = grabDatesFromTimesheets(timesheets)

    //Timesheets with dates that are not in the dates from the timecards
    const filteredTimesheets = getTimesheetsFromStore.value.filter((timesheet) => {
      return !dates.some((date) => {
        return (
          date.getDate() === new Date(timesheet.date).getDate() &&
          date.getMonth() === new Date(timesheet.date).getMonth()
        )
      })
    })

    const formattedNewTimesheets = timesheets.map((timesheet) => formatTimesheetFromSync(timesheet))

    const newData = [...filteredTimesheets, ...formattedNewTimesheets]
    const { setTimesheets } = useTimesheets()
    setTimesheets(newData, {skip: true})
    await TimesheetsModel().writeTimesheets(newData)
  }

  //check if timehseet is there for this day (probably today)
  const timesheetForDay = (date: Date = new Date(), timesheets: Timesheets) => {
    const timesheetDays = timesheets.map((ts) => new Date(ts.date))
    const day = new Date(date)
    return timesheetDays.some((tsd) => isSameDay(day, tsd))
  }
  const getTimesheetIndexByDate = (date: Date) => {
    const { getTimesheets: getTimesheetsFromStore } = storeToRefs(useTimesheets())
    return getTimesheetsFromStore.value.findIndex((ts) => isSameDay(new Date(ts.date), date))
  }
  const setTimesheetTimersAndLock = async (date: Date = new Date()) => {
    const { getTimesheets: getTimesheetsFromStore } = storeToRefs(useTimesheets())
    const { setTimers, lockTimerEdits, setOriginalTimers } = useTimers()
    const { swappedTimers } = storeToRefs(useTimers())
    const tsIndex = getTimesheetIndexByDate(date)
    if (tsIndex > -1) {
      //if we have swapped timers, we only want to set these timers to the
      //"original timers" placeholder. Note this is only in the case that
      //we're setting these timesheet for "today"
      if (swappedTimers.value) {
        const data = getTimesheetsFromStore.value[tsIndex].timers as unknown as Timer[]
        lockTimerEdits(data)
        setOriginalTimers(data)
      } else {
        //make sure index is real, set the timers
        await setTimers(
          [...getTimesheetsFromStore.value[tsIndex].timers] as unknown as Timer[],
          false,
          false,
          true //explicitly wipe timers
        )
        //since this came from a timecard, we have to lock it (as its already complete),
        //if they want to unlock it (to edit, or re-do rejected timecard) they
        //should do so from previous pay period page
        lockTimerEdits()
      }
    }
  }

  return {
    hasReadyToSyncTimesheets,
    getTimesheetsToSync,
    saveNewTimesheets,
    removeBeyondPayPeriod,
    timesheetForDay,
    setTimesheetTimersAndLock,
  }
}

const formatTimesheetFromSync = (timesheet: any): Timesheet => {
  const {
    toBePaidDistance,
    toBePaidHours,
    time,
    expenses,
    orders,
    timers,
    expensesData,
    editHistory,
    ...rest
  } = timesheet
  return {
    ...rest,
    timers: JSON.parse(timers),
    toBePaidDistance: Number(toBePaidDistance),
    toBePaidHours: Number(toBePaidHours),
    time: Number(time),
    expenses: Number(expenses),
    orders: Number(orders),
    expensesData: JSON.parse(expensesData),
    modifiedByRep: false,
    ...(timesheet.editHistory && { editHistory: JSON.parse(editHistory) }),
  }
}
